import invariant from 'invariant';
import assertNever from '@watershed/shared-util/src/assertNever';
import { ASTExpr, ASTProgram, ASTStmt } from '@watershed/lscript/ast';

// Recursively visit all subexpressions of an expression, depth first, passing
// each one into a visitor function.  If the visitor function returns true
// The expression will be yielded.

export function* visitSubexpressions(
  expr: ASTExpr,
  traversal: 'preorder' | 'postorder' = 'preorder'
): Generator<ASTExpr> {
  if (traversal === 'preorder') yield expr;
  switch (expr.t) {
    case 'number':
    case 'string':
    case 'boolean':
    case 'null':
    case 'identifier':
      break;
    case 'unary':
      yield* visitSubexpressions(expr.expr, traversal);
      break;
    case 'binary':
    case 'comparison':
      yield* visitSubexpressions(expr.l, traversal);
      yield* visitSubexpressions(expr.r, traversal);
      break;
    case 'call':
      for (const arg of expr.args) {
        yield* visitSubexpressions(arg, traversal);
      }
      for (const { expr: kwExpr } of expr.kwargs.values()) {
        yield* visitSubexpressions(kwExpr, traversal);
      }
      break;
    default:
      assertNever(expr);
  }
  if (traversal === 'postorder') yield expr;
}

// Recursively visit all subexpressions of a variable from a program.  When it
// would report an identifier expression, instead returns the statement that
// defines that variable.
export function* visitSubstatements(
  program: ASTProgram,
  varOrStmtOrExpr: string | ASTStmt | ASTExpr,
  traversal: 'preorder' | 'postorder' = 'preorder',
  allVars: Map<string, ASTStmt> = new Map<string, ASTStmt>()
): Generator<ASTExpr | ASTStmt> {
  // This is the top level of the recursion.  If we're given a string, it's the
  // name of the variable we're looking for.
  if (typeof varOrStmtOrExpr === 'string') {
    // Loop through the statements in the program and add them to the map.  When it
    // finds the one we're looking for, we should already have all referenced variables
    // in the map.
    let rootVar = allVars.get(varOrStmtOrExpr);
    for (const stmt of program) {
      allVars.set(stmt.name, stmt);
      if (stmt.name === varOrStmtOrExpr) {
        rootVar = stmt;
        break;
      }
    }

    invariant(
      rootVar != null,
      `Could not find variable ${varOrStmtOrExpr} in program`
    );

    yield* visitSubstatements(program, rootVar, traversal, allVars);
  } else if (varOrStmtOrExpr.t === 'assign') {
    if (traversal === 'preorder') yield varOrStmtOrExpr;
    yield* visitSubstatements(
      program,
      varOrStmtOrExpr.expr,
      traversal,
      allVars
    );
    if (traversal === 'postorder') yield varOrStmtOrExpr;
  } else {
    if (traversal === 'preorder') yield varOrStmtOrExpr;

    switch (varOrStmtOrExpr.t) {
      case 'number':
      case 'string':
      case 'boolean':
      case 'null':
        break;
      case 'identifier':
        const stmt = allVars.get(varOrStmtOrExpr.name);
        invariant(
          stmt,
          `Could not find variable ${varOrStmtOrExpr.name} while traversing`
        );
        yield* visitSubstatements(program, stmt, traversal, allVars);
        break;
      case 'unary':
        yield* visitSubstatements(
          program,
          varOrStmtOrExpr.expr,
          traversal,
          allVars
        );
        break;
      case 'binary':
      case 'comparison':
        yield* visitSubstatements(
          program,
          varOrStmtOrExpr.l,
          traversal,
          allVars
        );
        yield* visitSubstatements(
          program,
          varOrStmtOrExpr.r,
          traversal,
          allVars
        );
        break;
      case 'call':
        for (const arg of varOrStmtOrExpr.args) {
          yield* visitSubstatements(program, arg, traversal, allVars);
        }
        for (const { expr: kwExpr } of varOrStmtOrExpr.kwargs.values()) {
          yield* visitSubstatements(program, kwExpr, traversal, allVars);
        }
    }
    if (traversal === 'postorder') yield varOrStmtOrExpr;
  }
}
